import React, { useState } from 'react';
import { Card, Button, Spinner } from 'react-bootstrap';
import { Newspaper, InfoCircle } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import useJurisdictionArticles from '../../hooks/useJurisdictionArticles';
import useCollections, { CollectionModal } from '../../hooks/useCollections';
import LoadMoreButton from '../feed/LoadMoreButton';
import openCapacitorSite from '../../utils/browserUtils';
import '../feed/FeedItem.css';
import '../feed/PersonalizedNewsReport.css';

// News, press releases and announcements archived from the city's own
// government website, via api2.govglance.org.
//
// Not the same thing as useLocalNews, which searches the web for coverage OF
// the city. This is the city government's own output, archived.
//
// A sibling to the report above it rather than part of it: the report is
// generated, this is the record it was generated from. Somebody who wants to
// check a claim in the report should be able to read the source on the same
// screen.
//
// ─── Why this borrows the report's markup rather than its own ─────────────────
//
// It is a second section of the same page, so it has to read as one. Every
// class here comes from PersonalizedNewsReport and FeedItem: the
// homepage-branch-carousel wrapper, the branch-header, report-section-wrapper,
// and feed-item--default cards. A parallel stylesheet would have been one more
// place to update every time the card design moves, and the two sections would
// have drifted apart the first time it did.
//
// Every item links to the address the government itself published. The archive
// keeps a resolved URL too, but the original is the one the city advertises, so
// it is the one a reader is sent to. Opened through openCapacitorSite so it
// behaves the same in the native shell as in the browser.

const PAGE_SIZE = 5;

// Same formatter the report uses, for the same reason: a date string is parsed
// as UTC midnight and renders as the previous day everywhere west of
// Greenwich, so the offset is added back before the parts are read.
const formatDate = (inputDate) => {
  if (!inputDate) return '';
  const date = new Date(inputDate);
  if (Number.isNaN(date.getTime())) return '';
  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  return `${utcDate.getMonth() + 1}-${utcDate.getDate()}-${utcDate.getFullYear()}`;
};

const truncateText = (text, maxLength = 200) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

const JurisdictionNewsFeed = ({ city, stateAbbr, limit = 20, isFirstCarousel = false }) => {
  const { t } = useTranslation('common');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const { jurisdiction, articles, loading, error } = useJurisdictionArticles({
    city,
    stateAbbr,
    limit,
  });

  const {
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    closeModal,
    handleSave,
    isStorageAvailable,
    CollectionButton,
  } = useCollections();

  if (!city || !stateAbbr) return null;

  const sectionTitle = t('common:localGovernmentNews', 'Local Government News');
  const visible = articles.slice(0, visibleCount);

  const buildCollectionItem = (article) => ({
    title: article.title,
    url: article.url,
    date: article.publishedAt,
    feedName: sectionTitle,
    branch: 'Local News',
    topic: 'local',
  });

  const header = (
    <div className="branch-header">
      <div className="branch-title report-branch-title">
        <Newspaper className="report-icon" />
        <span>{sectionTitle}</span>
      </div>
    </div>
  );

  const wrapperClass = `homepage-branch-carousel ${
    isFirstCarousel ? 'homepage-first-carousel' : ''
  }`;

  if (loading) {
    return (
      <div className={wrapperClass}>
        {header}
        <div className="report-loading-container">
          <Spinner animation="grow" variant="light" />
        </div>
      </div>
    );
  }

  // Three empty screens that look alike and mean different things, said apart
  // rather than collapsed into one sentence. A city the archive has never heard
  // of is a coverage gap; a city it knows but has not crawled is a queue.
  let emptyMessage = null;
  if (error) {
    emptyMessage = t(
      'common:localNewsUnavailable',
      'Local government news is unavailable right now.'
    );
  } else if (!jurisdiction) {
    emptyMessage = t(
      'common:cityNotArchived',
      '{{city}} is not in the local archive yet.',
      { city }
    );
  } else if (articles.length === 0) {
    emptyMessage = t(
      'common:cityNotCrawled',
      'Nothing has been collected from this government website yet.'
    );
  }

  if (emptyMessage) {
    return (
      <div className={wrapperClass}>
        {header}
        <div className="report-section-wrapper">
          <p className="report-no-content report-no-content--centered">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      {header}

      <div className="report-section-wrapper">
        {visible.map((article, index) => {
          const openPage = () => article.url && openCapacitorSite(article.url, '_blank');

          return (
            <div key={article.key || index} className="feed-item feed-item--default mb-4">
              <Card>
                <Card.Title
                  id="billText"
                  className="card-title p-3"
                  onClick={openPage}
                  style={{ cursor: article.url ? 'pointer' : 'default' }}
                >
                  {truncateText(article.title, 200)}
                </Card.Title>

                {/* The date sits where the report's updates put it. The
                    category is folded in beside it rather than given a row of
                    its own, so the card keeps the same height as every other
                    card in the column. */}
                <Card.Body id="billBodyText">
                  {[article.categoryLabel, formatDate(article.publishedAt)]
                    .filter(Boolean)
                    .join(' · ')}
                </Card.Body>

                <Card.Footer className="d-flex">
                  <Button className="clickable me-2" id="billLink" onClick={openPage}>
                    <InfoCircle /> {t('common:Source', 'Source')}
                  </Button>
                  <CollectionButton item={buildCollectionItem(article)} />
                </Card.Footer>
              </Card>
            </div>
          );
        })}

        {/* The shared component rather than its markup copied here, so this
            button cannot drift from the one under every other paged feed.
            `loading` is false by definition: paging is a slice of rows already
            held, and the whole section returns the spinner above while the
            fetch is in flight. */}
        {visibleCount < articles.length && (
          <LoadMoreButton
            onLoadMore={() => setVisibleCount((count) => count + PAGE_SIZE)}
            loading={false}
          />
        )}
      </div>

      <CollectionModal
        isModalOpen={isModalOpen}
        currentItem={currentItem}
        saveSuccess={saveSuccess}
        note={note}
        setNote={setNote}
        handleSave={handleSave}
        closeModal={closeModal}
        isStorageAvailable={isStorageAvailable}
      />
    </div>
  );
};

export default JurisdictionNewsFeed;